import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _isEmpty from "lodash/isEmpty";
import _toUpper from "lodash/toUpper";

var _templateObject, _templateObject2;

import React from 'react';
import ClassNames from 'classnames';
import PropTypes from 'prop-types';
import Image from '@nerdwallet/react-image';
import { Type } from '@nerdwallet/currency';
import { gql, useQuery } from '@nerdwallet/apollo';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import MarketplaceSearch from '../marketplace-search';
import styles from './index.module.less';
import { PLACEHOLDER, getErrorMessage, NAMED_QUERY, OFFER_FIELD } from '../lib/product-constants';

var renderSuggestion = function renderSuggestion(suggestion) {
  var _suggestion$image, _suggestion$image2;

  return /*#__PURE__*/React.createElement("div", {
    className: ClassNames(styles.suggestionWrapper)
  }, /*#__PURE__*/React.createElement(Image, {
    src: suggestion === null || suggestion === void 0 ? void 0 : (_suggestion$image = suggestion.image) === null || _suggestion$image === void 0 ? void 0 : _suggestion$image.source,
    alt: suggestion === null || suggestion === void 0 ? void 0 : (_suggestion$image2 = suggestion.image) === null || _suggestion$image2 === void 0 ? void 0 : _suggestion$image2.alt,
    className: styles.suggestionImage,
    naturalHeight: 37.5,
    naturalWidth: 60,
    objectFit: "contain",
    backgroundColor: "transparent"
  }), /*#__PURE__*/React.createElement(Type, {
    className: styles.suggestionText,
    dangerouslySetInnerHTML: {
      __html: suggestion.displayName
    }
  }));
};

export var getAutoCompleteQuery = function getAutoCompleteQuery(searchFragment) {
  return gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n  query marketplaceAutocompleteData(\n    $isFuzzy: Boolean\n    $keyword: String!\n    $namedQuery: String\n    $limit: Int\n    $productType: String!\n    $queryFields: [String!]\n    $sort: String\n  ) {\n    marketplaceAutocompleteSearch(\n      isFuzzy: $isFuzzy\n      keyword: $keyword\n      namedQuery: $namedQuery\n      limit: $limit\n      productType: $productType\n      queryFields: $queryFields\n      sort: $sort\n    ) {\n      ... on MarketplaceEntityInterface {\n        id\n      }\n\n      ... on MarketplaceEntityWithPlacementInterface {\n        displayName\n        image {\n          alt\n          source\n        }\n      }\n\n      ...SearchFields\n    }\n  }\n"])), searchFragment);
};

var CreditCardSearch = function CreditCardSearch(_ref) {
  var analytics = _ref.analytics,
      autocompleteQuery = _ref.autocompleteQuery,
      defaultValue = _ref.defaultValue,
      label = _ref.label,
      namedQuery = _ref.namedQuery,
      onSelectHandler = _ref.onSelectHandler,
      placeholder = _ref.placeholder,
      searchFragment = _ref.searchFragment;

  var _React$useState = React.useState(defaultValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var productType = PRODUCT_TYPES.CREDIT_CARDS;
  var queryVariables = {
    isFuzzy: true,
    keyword: value,
    namedQuery: namedQuery,
    productType: _toUpper(productType),
    queryFields: autocompleteQuery,
    sort: OFFER_FIELD.DETAIL_POPULARITY_RANK
  };
  var searchQuery = getAutoCompleteQuery(searchFragment);

  var _useQuery = useQuery(searchQuery, {
    variables: queryVariables,
    skip: _isEmpty(value),
    fetchPolicy: 'no-cache'
  }),
      loading = _useQuery.loading,
      error = _useQuery.error,
      data = _useQuery.data,
      refetch = _useQuery.refetch;

  var suggestions = data === null || data === void 0 ? void 0 : data.marketplaceAutocompleteSearch;

  var hasError = !!error || !loading && !_isEmpty(value) && _isEmpty(suggestions);

  return /*#__PURE__*/React.createElement(MarketplaceSearch, {
    analytics: analytics,
    error: hasError,
    errorMessage: getErrorMessage(value, productType),
    id: "credit-card-search",
    label: label,
    onSelectHandler: onSelectHandler,
    placeholder: placeholder,
    productType: productType,
    queryVariables: queryVariables,
    refetch: refetch,
    renderSuggestion: renderSuggestion,
    setValue: setValue,
    suggestions: suggestions,
    value: value
  });
};

CreditCardSearch.displayName = 'CreditCardSearch';
CreditCardSearch.propTypes = {
  /**
   * analytics properties
   */
  analytics: PropTypes.shape({
    entityName: PropTypes.string,
    formName: PropTypes.string,
    sectionName: PropTypes.string,
    sectionPosition: PropTypes.number
  }),

  /**
   * Query for the autocomplete search to index on.
   */
  autocompleteQuery: PropTypes.arrayOf(PropTypes.string),

  /**
   * Optional to set default value of the input.
   */
  defaultValue: PropTypes.string,

  /**
   * The description displayed above the input.
   */
  label: PropTypes.string || PropTypes.node,

  /**
   * Specify a specific namedQuery.
   */
  namedQuery: PropTypes.string,

  /**
   * A onclick handler that gives access to the selected suggestion.
   * Passed to the base component.
   */
  onSelectHandler: PropTypes.func.isRequired,

  /**
   * The text displayed inside the input when no value is entered.
   */
  placeholder: PropTypes.string,

  /**
   * Fragment to return additional fields.
   */
  searchFragment: PropTypes.any // eslint-disable-line react/forbid-prop-types

};
CreditCardSearch.defaultProps = {
  defaultValue: '',
  label: '',
  namedQuery: NAMED_QUERY.CREDIT_CARDS,
  placeholder: PLACEHOLDER.CREDIT_CARDS,
  autocompleteQuery: [OFFER_FIELD.PRODUCT_NAME, OFFER_FIELD.PRODUCT_INSTITUTION_NAME, OFFER_FIELD.DETAILS_SLUG],
  searchFragment: gql(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    fragment SearchFields on CreditCardsOfferV2 {\n      legacyId\n    }\n  "])))
};
export default CreditCardSearch;